import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Instagram, MessageCircle } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import InquiryForm from "@/components/InquiryForm";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: "easeOut" },
};

const CHANNELS = [
  { icon: MapPin, label: "Dove", value: "Rimini, Italia", href: null },
  { icon: Phone, label: "Telefono", value: "+39 339 230 3993", href: "tel:+393392303993", id: "phone" },
  { icon: Mail, label: "Email", value: "brunchbottega@gmail.com", href: "mailto:brunchbottega@gmail.com", id: "email" },
  { icon: Instagram, label: "Instagram", value: "@brunch_bottega", href: "https://www.instagram.com/brunch_bottega", id: "instagram" },
  { icon: MessageCircle, label: "WhatsApp", value: "+39 339 230 3993", href: "https://wa.me/393392303993?text=Ciao!%20Vi%20contatto%20dal%20sito%20di%20Brunch%20%26%20Bottega.", id: "whatsapp" },
];

export default function Contatti() {
  const [params] = useSearchParams();
  const prodotto = params.get("prodotto");
  const prefilledSubject = prodotto ? `Informazioni su: ${prodotto}` : "";

  useSeo({
    title: "Contatti | Brunch & Bottega Rimini",
    description: "Contatta Brunch & Bottega a Rimini per brunch a domicilio, eventi privati e prodotti artigianali. Ti rispondiamo noi, di persona.",
    path: "/contatti",
  });

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 sm:pt-20 sm:pb-28" data-testid="contatti-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: "easeOut" }} className="max-w-2xl">
        <p className="text-xs uppercase tracking-[0.25em] text-terracotta font-semibold mb-5">Contatti</p>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.1] text-ink">
          Parliamone <em className="text-forest">davanti a un caffè.</em> O qui.
        </h1>
        <p className="mt-6 text-base sm:text-lg leading-relaxed text-stone-600">
          Per un brunch, un evento, un prodotto della bottega o un'idea che ancora non sa di niente:
          scrivici e ti rispondiamo noi. Di persona, senza bot.
        </p>
      </motion.div>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.aside {...fadeUp} className="lg:col-span-4" data-testid="contatti-channels">
          <div className="bg-forest text-cream rounded-3xl p-8 shadow-lg">
            <h2 className="font-display text-2xl">Dove trovarci</h2>
            <ul className="mt-6 space-y-4">
              {CHANNELS.map((c, i) => (
                <li key={c.label} className="flex items-start gap-3" data-testid={`contact-channel-${i}`}>
                  <c.icon size={18} className="text-terracotta mt-0.5 shrink-0" aria-hidden="true" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] text-cream/60">{c.label}</p>
                    {c.href ? (
                      <a
                        href={c.href}
                        {...(c.href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                        className="text-sm sm:text-base font-medium hover:text-terracotta transition-colors"
                        data-testid={`contact-link-${c.id}`}
                      >
                        {c.value}
                      </a>
                    ) : (
                      <p className="text-sm sm:text-base font-medium">{c.value}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            <p className="mt-7 text-xs leading-relaxed text-cream/60">
              Rispondiamo di persona, di solito entro un giorno. Per le cose urgenti, WhatsApp è la via più veloce.
            </p>
          </div>
        </motion.aside>

        <motion.div {...fadeUp} className="lg:col-span-8 bg-white/80 border border-stone-200/80 rounded-3xl p-6 sm:p-10 shadow-sm" data-testid="contatti-form-wrapper">
          <h2 className="font-display text-2xl text-ink mb-7">Scrivici due righe</h2>
          <InquiryForm
            key={prefilledSubject}
            kind={prodotto ? "prodotto" : "contatto"}
            variant="contact"
            submitLabel="Invia il messaggio"
            prefilledSubject={prefilledSubject}
            testIdPrefix="contatti"
          />
        </motion.div>
      </div>
    </section>
  );
}
